import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/observable/forkJoin';
import { HttpClient } from '@angular/common/http';

import { Question } from '../../models/question.class';
import { Entry } from '../../models/entry.class';

/**
 * DataEnadisService
 * Provides data read from sources
 */
@Injectable()
export class DataEnadisService {
  /** List of questions */
  codebook: Array<Question>;
  /** Defined groups for ENADIS survey, based on populations */
  groups = [
    {
      'name': 'Población de 18 años y más (opiniones y experiencias de discriminación)',
      'slug': 'Cuestionario de opiniones y experiencias',
      'collapseBy': 'FACTOR_COE',
      'dummy': 'dum_coe',
      'category': 'poblacion'
    }, // Validar si se agrega aqui o se cambia en la BD
    {
      'name': 'Población de 18 años y más (opiniones y experiencias de discriminación)',
      'slug': 'Cuestionario de opinión y experiencias',
      'collapseBy': 'FACTOR_COE',
      'dummy': 'dum_coe',
      'category': 'poblacion'
    }, {
      'name': 'Características sociodemográficas de la población',
      'slug': 'Características sociodemográficas',
      'collapseBy': 'FACTOR_SDE',
      'dummy': 'dum_sde',
      'category': 'poblacion'
    }, {
      'name': 'Personas indígenas',
      'slug': 'Personas indígenas',
      'collapseBy': 'FACTOR_IND',
      'dummy': 'dum_indigena',
      'category': 'modulo'
    }, {
      'name': 'Personas con discapacidad',
      'slug': 'Personas con discapacidad',
      'collapseBy': 'FACTOR_DIS',
      'dummy': 'dum_discapacidad',
      'category': 'modulo'
    }, {
      'name': 'Personas de la diversidad religiosa',
      'slug': 'Personas de la diversidad religiosa',
      'collapseBy': 'FACTOR_REL',
      'dummy': 'dum_nocatolico',
      'category': 'modulo'
    }, {
      'name': 'Personas afrodescendientes',
      'slug': 'Afrodescendientes',
      'collapseBy': 'FACTOR_SDE',
      'dummy': 'dum_afro',
      'category': 'modulo'
    }, {
      'name': 'Personas de la diversidad sexual',
      'slug': 'Personas no heterosexuales',
      'collapseBy': 'FACTOR_COE',
      'dummy': 'dum_nohetero',
      'category': 'modulo'
    }, {
      'name': 'Mujeres',
      'slug': 'Mujeres',
      'collapseBy': 'FACTOR_MUJ',
      'dummy': 'dum_mujer',
      'category': 'grupo'
    }, {
      'name': 'Trabajadoras del hogar remuneradas',
      'slug': 'Trabajadoras del hogar remuneradas',
      'collapseBy': 'FACTOR_MUJ',
      'dummy': 'dum_trabhog',
      'category': 'grupo'
    }, {
      'name': 'Niñas y niños (9 a 11 años)',
      'slug': 'Niñas y niños',
      'collapseBy': 'FACTOR_NIN',
      'dummy': 'dum_ninos',
      'category': 'grupo'
    }, {
      'name': 'Adolescentes y jóvenes (12 a 29 años)',
      'slug': 'Adolescentes y jóvenes',
      'collapseBy': 'FACTOR_ADO',
      'dummy': 'dum_adolescentes',
      'category': 'grupo'
    }, {
      'name': 'Personas mayores (60 años y más)',
      'slug': 'Personas adultas mayores',
      'collapseBy': 'FACTOR_ADU',
      'dummy': 'dum_adultomayor',
      'category': 'grupo'
    }];

  /** Inits enadis service, injects http client */
  constructor(private http: HttpClient) { }

  /** Returns the response to the question catalog. Sends a GET request, can return questions by only a selected topic */
  getCodebook(topic?: string): Observable<Question[]> {
    return this.http.get<Question[]>('/api/enadis/v1/question' + (topic ? '?topic=' + topic : ''));
  }

  /** Get only a question that matches selected question identifier */
  getQuestion(questionId: string): Observable<Question[]> {
    return this.http.get<Question[]>('/api/enadis/v1/question?questionId=' + questionId);
  }

  /** Get entries from database. Can get entries only for a selected question,
   * and return entries in a parsed mode
   * @param [questionId] Selected question identifier
   * @param [mode] If specified, entries should be returned in a parsed mode
  */
  getEntries(questionId?, mode?): Observable<Entry[]> {
    let url = '/api/enadis/v1/entry?';
    if (questionId) {
      url += 'questionId=' + questionId + '&';
    }
    if (mode) {
      url += 'mode=' + mode;
    }
    return this.http.get<Entry[]>(url);
  }

  /** Returns columns that correspond to selected question identifier */
  getColumnsFromQuestionId(questionId: string): Observable<Object[]> {
    return this.http.get<Object[]>('/api/enadis/v1/catalog/getColumnsFromQuestionId/' + questionId);
  }

  /** Returns categories (answers) that correspond to selected question identifier */
  getRowsFromQuestionId(questionId: string): Observable<Object[]> {
    return this.http.get<Object[]>('/api/enadis/v1/catalog/getRowsFromQuestionId/' + questionId);
  }

  /** Returns filter options that exist on database for a selected question and a specific column name */
  getFilterOptions(questionId: string, columnName: string): Observable<Object[]> {
    return this.http.get<Object[]>('/api/enadis/v1/entry/getFilterOptions?questionId=' + questionId + '&columnName=' + columnName);
  }

  /** Returns parsed data ready to display in a chart, based on selected question,
   * selected filters and options (axes, unit, collapse by)
   */
  getDataToDraw(questionId: string, selectedFilters: object, options: object): Observable<Entry[]> {
    const url = '/api/enadis/v1/entry/getDataToDraw';
    const params = {
      questionId: questionId,
      keys: options,
      selectedFilters: selectedFilters,
      dropNAs: true
    };
    return this.http.post<Entry[]>(url, params);
  }

  /** Returns parsed data ready to display in a chart, based on TWO selected questions
   * (for scatter functionalities). every question should specify it's own selected filters
   * and selected keys options
   */
  crossData(firstQuestion: any, secondQuestion: any): Observable<Entry[]> {
    const url = '/api/enadis/v1/entry/crossData';
    const params = {
      firstQuestion: firstQuestion,
      secondQuestion: secondQuestion
    };
    return this.http.post<Entry[]>(url, params);
  }

  /** Return defined groups of the survey */
  getGroups() {
    return this.groups;
  }

  /** Return a group based on the name of the group */
  findGroup(name: string) {
    return this.groups.find(g => g.name === name || g.slug === name);
  }

  /** Returns a list of questions that should be available in "Compara poblaciones" section */
  getCodebookForProfiles(): Observable<Question[]> {
    return this.http.get<Question[]>('/api/enadis/v1/question?showVariableInPerfiles=true');
  }

  /** Returns default filter options for "Compara poblaciones" section */
  getPerfilesFilterOptions() {
    return {
      columns: undefined,
      categories: undefined,
      SEXO: {
        field: 'SEXO',
        name: 'Sexo',
        options: [
          { field: '', name: 'Todos' },
          { field: 1, name: 'Hombre' },
          { field: 2, name: 'Mujer' },
        ]
      },
      educa2: {
        field: 'educa2',
        name: 'Escolaridad',
        options: [
          { field: 1, name: 'Sin escolaridad' },
          { field: 2, name: 'Hasta primaria incompleta' },
          { field: 3, name: 'Primaria completa' },
          { field: 4, name: 'Secundaria completa' },
          { field: 5, name: 'Media superior' },
          { field: 6, name: 'Superior' },
          { field: '', name: 'Todos' },
          { field: 99, name: 'No especificado' },
        ]
      },
      P3_9: {
        field: 'P3_9',
        name: '¿Habla una lengua indígena?',
        options: [
          { field: '', name: 'Todos' },
          { field: 1, name: 'Sí' },
          { field: 2, name: 'No' },
          { field: 9, name: 'No sabe' },
        ]
      },
      discapacidad: {
        field: 'discapacidad',
        name: 'Condición de discapacidad',
        options: [
          { field: '', name: 'Todos' },
          { field: 1, name: 'Sí' },
          { field: 0, name: 'No' },
        ]
      },
      ENT: {
        field: 'ENT',
        name: 'Entidad Federativa',
        options: [
          { field: '', name: 'Nacional' },
          { field: 1, name: 'Aguascalientes' },
          { field: 2, name: 'Baja California' },
          { field: 3, name: 'Baja California Sur' },
          { field: 4, name: 'Campeche' },
          { field: 7, name: 'Chiapas' },
          { field: 8, name: 'Chihuahua' },
          { field: 9, name: 'Ciudad de México' },
          { field: 5, name: 'Coahuila' },
          { field: 6, name: 'Colima' },
          { field: 10, name: 'Durango' },
          { field: 11, name: 'Guanajuato' },
          { field: 12, name: 'Guerrero' },
          { field: 13, name: 'Hidalgo' },
          { field: 14, name: 'Jalisco' },
          { field: 16, name: 'Michoacán' },
          { field: 17, name: 'Morelos' },
          { field: 15, name: 'México' },
          { field: 18, name: 'Nayarit' },
          { field: 19, name: 'Nuevo León' },
          { field: 20, name: 'Oaxaca' },
          { field: 21, name: 'Puebla' },
          { field: 22, name: 'Querétaro' },
          { field: 23, name: 'Quintana Roo' },
          { field: 24, name: 'San Luis Potosí' },
          { field: 25, name: 'Sinaloa' },
          { field: 26, name: 'Sonora' },
          { field: 27, name: 'Tabasco' },
          { field: 28, name: 'Tamaulipas' },
          { field: 29, name: 'Tlaxcala' },
          { field: 30, name: 'Veracruz' },
          { field: 31, name: 'Yucatán' },
          { field: 32, name: 'Zacatecas' },
        ]
      },
      identidad: {
        field: 'identidad',
        name: 'Adscripción Étnica',
        options: [
          { field: '', name: 'Toda la población' },
          { field: 'P3_12', name: 'Indígena' },
          { field: 'P3_8', name: 'Afrodescendiente' },
          { field: 'BOTH', name: 'Ambos' },
        ]
      },
      RANGO_EDAD: {
        field: 'RANGO_EDAD',
        name: 'Grupo de edad',
        options: [
          { field: '', name: 'Todos' },
          { field: '6 a 11 años', name: '6 a 11 años' },
          { field: '9 a 11 años', name: '9 a 11 años' },
          { field: '12 a 17 años', name: '12 a 17 años' },
          { field: '18 a 29 años', name: '18 a 29 años' },
          { field: '30 a 59 años', name: '30 a 59 años' },
          { field: '60 o más años', name: '60 o más años' },
        ]
      },
      AGE_15: {
        field: 'AGE_15',
        name: 'Grupo de edad',
        options: [
          { field: '', name: 'Todos' },
          { field: '15 a 59', name: '15 a 59' },
          { field: '60 o más', name: '60 o más' },
        ]
      },
      AGE_6_24: {
        field: 'AGE_6_24',
        name: 'Grupo de edad',
        options: [
          { field: '', name: 'Todos' },
          { field: '6 a 14', name: '6 a 14' },
          { field: '15 a 24', name: '15 a 24' },
        ]
      },
      P3_8: {
        field: 'P3_8',
        name: 'Afrodescendiente',
        options: [
          { field: '', name: 'Todos' },
          { field: 1, name: 1 },
        ]
      },
      P3_12: {
        field: 'P3_12',
        name: 'Indígena',
        options: [
          { field: '', name: 'Todos' },
          { field: 1, name: 'Sí' },
        ]
      },
      COLOR_PIEL: {
        field: 'COLOR_PIEL',
        name: 'Tono de piel',
        options: [
          { field: '', name: 'Todos' },
          { field: 'Más obscuro', name: 'Más obscuro' },
          { field: 'Intermedio', name: 'Intermedio' },
          { field: 'Más claro', name: 'Más claro' },
        ]
      },
      TLOC: {
        field: 'TLOC',
        name: 'Tamaño de localidad',
        options: [
          { field: '', name: 'Todos' },
          { field: 1, name: 'Más de 100,000 habitantes' },
          { field: 2, name: 'De 15,000 a 99,999 habitantes' },
          { field: 3, name: 'De 2,500 a 14,999 habitantes' },
          { field: 4, name: 'Menos de 2,500 habitantes' }
        ]
      },
    };
  }
}



// WEBPACK FOOTER //
// ./src/app/services/data-enadis/data-enadis.service.ts